import autobind from 'class-autobind';

import BetType from 'atg/api/BetType';
import ReductionMisc from 'atg/api/ReductionMisc';
import { DIVIDENDS } from 'atg/constants/BetUpdateType';
import { RACE_INFO } from 'atg/constants/PopupMenuTypes';
import PopupInfo from 'atg/PopupInfo';
import PopupMenu from 'atg/PopupMenu';
import { showLockedAlertDialog } from 'atg/redux/actions';
import * as device from 'atg/utils/device';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import {
  showAlertDialog,
  showConfirmDialog,
  showGuaranteeAlertDialog,
  showManageHorseDialog,
} from 'shared/redux/actions';
import {
  monitor,
  settings,
  shares as shareSelectors,
  user,
} from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { formatDateISO } from 'shared/utils/dateUtils';
import { formatMoney } from 'shared/utils/money';
import Sentry from 'shared/utils/sentry';
import * as Text from 'shared/utils/Text';

const $ = window.$;

export default class BaseView {
  constructor(delegate, index) {
    autobind(this, BaseView.prototype);
    this.delegate = delegate;
    this.index = index;
    this.isDisabled = false;
  }

  isInstanceOf(gridClassName) {
    return this.getGridClass() === gridClassName;
  }

  isEqualToView(view) {
    return (
      this.constructor.name === view.constructor.name &&
      this.getIndex() === view.getIndex()
    );
  }

  getIsDisabled() {
    return this.isDisabled;
  }

  setIsDisabled(isDisabled) {
    this.isDisabled = isDisabled;
  }

  isBetLocked() {
    return this.getAPI().betInformation.getBetConfig().isLocked();
  }

  getCollaborators() {
    return shareSelectors.getCollaboratorsList(getStore().getState(), {
      betId: this.getAPI().betInformation.getID(),
      type: 'atg',
    });
  }

  isCollaborativeBet() {
    return this.getCollaborators()?.length >= 2;
  }

  isBetClaimedByAnotherUser() {
    const claimStatus = shareSelectors.getClaimStatus(getStore().getState(), {
      betId: this.getAPI().betInformation.getID(),
      type: 'atg',
    });
    const myId = this.getCollaborators()?.find(
      collaborator => collaborator.self,
    )?.id;
    return claimStatus?.claimed && claimStatus.currentOwner !== myId;
  }

  collabUserIsReadOnly() {
    const myPermission = shareSelectors.getMyPermission(getStore().getState(), {
      betId: this.getAPI().betInformation.getID(),
      type: 'atg',
    });
    return this.isCollaborativeBet() && myPermission === 'READ';
  }

  collabUserHasNoSubscription() {
    return (
      this.isCollaborativeBet() && !user.hasSubscription(getStore().getState())
    );
  }

  isBetBlocked() {
    return (
      this.isBetLocked() ||
      this.collabUserIsReadOnly() ||
      this.collabUserHasNoSubscription() ||
      this.isBetClaimedByAnotherUser()
    );
  }

  getGuaranteeValue() {
    return this.getAPI()
      .betInformation.getBetConfig()
      .getNumberOfErrorsGuarantee();
  }

  // Showing alert for all blocking scenarios:
  // - Locked bet
  // - Collaborative bet where current user only has read-only access
  // - Collaborative bet where current user has no paid subscription
  // - Collaborative bet currently claimed by another user
  // Optional array parameter to exclude certain checks, values:
  // LOCKED | READ_ONLY | NO_SUBSCRIPTION | CLAIMED
  // Returns true if blocked
  showAlertWhenLocked({ excludedStates = [] } = {}) {
    if (this.isBetLocked() && !excludedStates.includes('LOCKED')) {
      getStore().dispatch(showLockedAlertDialog());
      return true;
    }
    if (this.collabUserIsReadOnly() && !excludedStates.includes('READ_ONLY')) {
      getStore().dispatch(
        showAlertDialog({
          title: Text.get('read-only-headline'),
          details: Text.get('read-only-body'),
        }),
      );
      return true;
    }
    if (
      this.collabUserHasNoSubscription() &&
      !excludedStates.includes('NO_SUBSCRIPTION')
    ) {
      getStore().dispatch(
        showConfirmDialog({
          onConfirm: () => {
            Utils.callDelegate(this.delegate, 'onUpgradeMenuItemPressed');
          },
          title: Text.get('missing-subscription-headline'),
          details: Text.get('missing-subscription-body'),
          positive: Text.get('missing-subscription-upgrade-button'),
        }),
      );
      return true;
    }
    if (
      this.isBetClaimedByAnotherUser() &&
      !excludedStates.includes('CLAIMED')
    ) {
      getStore().dispatch(
        showAlertDialog({
          title: Text.get('claimed-by-other-headline'),
          details: Text.get('claimed-by-other-body'),
        }),
      );
      return true;
    }
    return false;
  }

  showAlertWhenGuaranteeSet() {
    const guaranteeValueNotDefault = this.getGuaranteeValue() !== 0;

    if (guaranteeValueNotDefault) {
      getStore().dispatch(showGuaranteeAlertDialog());
    }

    return guaranteeValueNotDefault;
  }

  // ---------------------------------------- DELEGATE ----------------------------------------

  getGrid() {
    return Utils.callDelegate(this.delegate, 'getViewGrid');
  }

  getAPI() {
    return Utils.callDelegate(this.delegate, 'getViewAPI');
  }

  getBottomInfo() {
    return Utils.callDelegate(this.delegate, 'getViewBottomInfo');
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return 'VIEW?';
  }

  getMenuTitle() {
    return 'VIEW?';
  }

  getMenuGroupTitle() {
    return 'VIEW GROUP?';
  }

  getMenuAddItemTitle() {
    return 'ADD ITEM?';
  }

  getMenuSelected() {
    const item = this.getItem();
    return item && !item.isEmpty();
  }

  getMenuTogglable() {
    return false;
  }

  getMenuToggled() {
    if (this.getMenuTogglable()) {
      const item = this.getItem();
      return item && !item.isDisabled();
    }
    return false;
  }

  getMenuDeletable() {
    return false;
  }

  // ---------------------------------------- DATA ----------------------------------------

  getItem() {
    return null;
  }

  addItem() {
    return null;
  }

  getNumberOfItems() {
    return 0;
  }

  toggleItem() {
    this.getItem().toggleDisabled();
    this.getAPI().scheduleBetUpdate();
  }

  deleteItem() {
    this.getItem().reset();
    this.getAPI().scheduleBetUpdate();
  }

  getLeftInfo(gameRow, startIndex) {
    let v, leftInfo1, leftInfo2;

    if (settings.showMarkPercent(getStore().getState())) {
      v = gameRow.getMarkPercent(startIndex);
      if (v != null) {
        leftInfo1 = Utils.compactPercent(v);
      }
    }

    if (!leftInfo1) {
      v = gameRow.getTrioIndex(startIndex);
      if (v != null) {
        leftInfo1 = v;
      }
    }

    if (settings.showOdds(getStore().getState())) {
      v = gameRow.getOdds(startIndex);
      if (v != null) {
        leftInfo2 = v;
      }
    }

    return { leftInfo1, leftInfo2 };
  }

  getRightInfo(gameRow, startIndex) {
    const shoeInfo = gameRow.getShoeInfo(startIndex);
    const sulkyInfo = gameRow.getSulkyInfo(startIndex);
    let html;
    let tooltip = '';

    if (shoeInfo && shoeInfo.isAvailable()) {
      const horseShoe = 'c';
      const overlaySlash = '<span class="overlay-slash">/</span>';

      html = '';

      if (!shoeInfo.getForeshoes()) {
        html += overlaySlash;
      }

      html += horseShoe;

      if (!shoeInfo.getHindshoes()) {
        html += overlaySlash;
      }

      html += horseShoe;

      tooltip += shoeInfo.hasLastRaceForeShoes()
        ? shoeInfo.getLastRaceForeShoes()
          ? '⊂'
          : '⊄'
        : '-';

      tooltip += shoeInfo.hasLastRaceHindShoes()
        ? shoeInfo.getLastRaceHindShoes()
          ? '⊂'
          : '⊄'
        : tooltip !== '-'
          ? '-'
          : '';
    } else {
      html = '-';
    }

    const rightInfo1 = $('<span />')
      .html(html)
      .addClass(
        shoeInfo &&
          shoeInfo.isAvailable() &&
          shoeInfo.getShoesChanged() &&
          !gameRow.getIsFirstStart(startIndex)
          ? 'shoe-info changed'
          : 'shoe-info',
      );

    rightInfo1.attr('title', tooltip);

    if (sulkyInfo && sulkyInfo.isAvailable) {
      html = sulkyInfo.getSulkyCode();
      tooltip = sulkyInfo.getLastRaceSulkyCode()
        ? sulkyInfo.getLastRaceSulkyCode()
        : '-';
    } else {
      html = '-';
    }

    const rightInfo2 = $('<span />')
      .html(html)
      .addClass(
        sulkyInfo &&
          sulkyInfo.isAvailable &&
          sulkyInfo.getSulkyChange() &&
          !gameRow.getIsFirstStart(startIndex)
          ? 'wagon-info changed'
          : 'wagon-info',
      );

    rightInfo2.attr('title', tooltip);

    return {
      rightInfo1,
      rightInfo2,
    };
  }

  getGameRowCellInfo(gameRow, startIndex) {
    if (!gameRow) {
      return {};
    }
    const info = {
      scratched: gameRow.isScratched(startIndex),
      ...this.getLeftInfo(gameRow, startIndex),
    };

    const { rightInfo1, rightInfo2 } = this.getRightInfo(gameRow, startIndex);

    if (settings.showBalance(getStore().getState())) {
      info.rightInfo1 = rightInfo1;
    }

    if (settings.showWagon(getStore().getState())) {
      info.rightInfo2 = rightInfo2;
    }
    return info;
  }

  changeOwnReductionOrder(row, indexMap) {
    const betRow = this.getAPI().betInformation.getRow(row);
    const newArray = [];

    if (betRow.getOwnReductionPercentages().length === 0) {
      return;
    }

    for (const element of indexMap) {
      newArray.push(betRow.getOwnReductionPercentage(element));
    }
    for (let index = 0; index < indexMap.length; index++) {
      betRow.setOwnReductionPercentage(index, newArray[index]);
    }
  }

  // ---------------------------------------- FORM ----------------------------------------

  onFormSubmit() {
    $(document.activeElement).blur();
    return false;
  }

  attachNegativeBlankEvents($container) {
    $container.on('input', 'input[type=number]', function (e) {
      const $currentTarget = $(e.currentTarget),
        currentNumber = $currentTarget.val();

      if (0 > currentNumber) {
        $currentTarget.val('');
        $currentTarget.trigger('input');
      }
    });
  }

  // ---------------------------------------- TOOLBAR ----------------------------------------
  hasToolbar() {
    return false;
  }

  getToolbarContent() {
    return {};
  }
  // ---------------------------------------- GRID DELEGATE METHODS ----------------------------------------

  onHeaderRender() {}
  getGridCellInfo() {}

  getGridHeader(column) {
    return column + 1;
  }

  onGridCellDoubleTapped(position) {
    this.onGridLastButtonPressed(position);
  }

  onRowWasUpdated() {
    return true;
  }

  requestDividendsInUpdate() {
    return false;
  }

  onGridFirstCellPressed(row) {
    const api = this.getAPI();

    const betTypeCode = api.raceInformation.getBetType();
    const betType = BetType.getBetType(betTypeCode);

    const position = {
      left: this.getGrid().getColumnLeft(0),
      top: this.getGrid().getRowTop(row, true),
    };

    const nr = row + 1;
    const items = [];
    const race = api.raceInformation.getRace(row);

    if (race) {
      const externalURL = race.getExternalURL();
      if (externalURL) {
        items.push(
          {
            title: Text.get('grid-row-menu-show-start-list'),
            href: externalURL,
          },
          '--------------------',
        );
      }
    }

    items.push({
      title: betType.hasSingleRace()
        ? Text.get('grid-row-menu-show-horse-info-no-legs')
        : Text.get('grid-row-menu-show-horse-info', nr),
      onPress: function () {
        Utils.callDelegate(this.delegate, 'onViewHorseInfoRacePressed', row);
        PopupMenu.closeCurrent();
      }.bind(this),
    });

    if (
      betType &&
      betType.hasRaceAnalysis() &&
      !Utils.callDelegate(this.delegate, 'isSharedBetActive')
    ) {
      items.push('--------------------', {
        title: Text.get('grid-row-menu-show-loading-race-analysis'),
        loading: true,
        color: 'red',
      });
      const popup = new PopupMenu(RACE_INFO, position, items);
      api.loadRaceAnalysis(
        race.getLegNumber(),
        race.getRaceKey(),
        raceAnalysis => {
          let item;
          if (raceAnalysis && raceAnalysis.isAvailable()) {
            item = {
              color: 'red',
              onPress: function () {
                Utils.callDelegate(this.delegate, 'onRaceAnalysisPressed', row);
                PopupMenu.closeCurrent();
              }.bind(this),
            };
            item.title = Text.get('grid-row-menu-show-race-analysis');
            items[items.length - 1] = item;
            popup.setItems(items);
          } else {
            item = {
              title: Text.get('grid-row-menu-show-no-race-analysis'),
              color: 'red',
            };
            items[items.length - 1] = item;
            popup.setItems(items);
          }
        },
      );
    } else {
      new PopupMenu(RACE_INFO, position, items);
    }
  }

  onGridFirstCellHover(row, showPopup) {
    if (device.isTouch()) {
      return;
    }

    if (this._firstCellPopupInfo && showPopup === false) {
      this._firstCellPopupInfo.close();
      return;
    }

    const api = this.getAPI();
    const raceDetails = api.raceInformation.getRace(row).getRaceDetails();

    const position = {
      left: this.getGrid().getColumnLeft(0),
      top: this.getGrid().getRowTop(row, true),
    };

    this._firstCellPopupInfo = new PopupInfo(raceDetails, position, 600);
  }

  onGridHighlightClosed() {}

  getGridNumberOfGridRows() {
    return this.getAPI().raceInformation.getNumberOfRaces();
  }

  getGridNumberOfGridColumns() {
    let columns = 0;

    for (
      let race = 0;
      race < this.getAPI().raceInformation.getNumberOfRaces();
      race++
    ) {
      columns = Math.max(
        columns,
        this.getAPI().raceInformation.getRace(race).getNumberOfStarts(),
      );
    }
    return columns;
  }

  getGridNumberOfRowColumns(row) {
    const race = this.getAPI().raceInformation.getRace(row);
    return race ? race.getNumberOfStarts() : 0;
  }

  getGridRowInfo(rowIndex) {
    const api = this.getAPI();
    const raceInformation = api.raceInformation;
    const betInformation = api.betInformation;
    const nrOfBetsRow = api.stake.getNrOfBetsRow(rowIndex);
    const numRaces = raceInformation.getNumberOfRaces();

    // Exit early if we don't have any races (yet)
    if (numRaces === 0) {
      // Return an empty object to reduce risk of something breaking
      return {};
    }

    if (rowIndex > numRaces - 1) {
      const errMsg = `Invalid rowIndex argument: '${rowIndex}'. Expected value between 0-${
        numRaces - 1
      } (${numRaces} races).`;
      Sentry.captureException(new Error(errMsg));
      console.error(errMsg);
      return {};
    }

    const race = raceInformation.getRace(rowIndex);
    const row = betInformation.getRow(rowIndex);
    const legNumber = rowIndex + 1;

    const info = {
      time: '-',
      title: legNumber,
      info: '-',
    };

    if (raceInformation) {
      const betType = raceInformation.getBetType();
      if (betType) {
        if (betType === 'T' || betType === 'FIVEPLUS') {
          // In Trio/Trippel & 5+, we display 1:a, 2:a, 3:a (4:a, 5:a)
          info.title = `${legNumber}:a`;
        } else {
          const betTypeTextID = `bet-type-short_${betType.toLowerCase()}`;
          const betTypeString = Text.hasText(betTypeTextID)
            ? Text.get(betTypeTextID)
            : betType;

          info.title = `${betTypeString}-${legNumber}`;
        }
      }

      if (race) {
        info.raceNumber = race.getRaceNumber();

        const time = race.getTime();
        info.time = time ? Utils.time(time, true) : '-';

        const distance = race.getDistance();
        const startMethod = race.getStartMethod()
          ? race.getStartMethod().toLowerCase()
          : '';
        const monte = race.getIsMonte() ? 'Monté' : '';

        const startInfoParts = [distance, startMethod, monte].filter(Boolean);

        info.info = startInfoParts.join(' ') || '-';
      }
    }

    if (row) {
      info.numberOfHorses = row.getNumberOfHorses();
    }

    info.nrOfBetsRow = nrOfBetsRow;

    return info;
  }

  getHorseAtPosition(position) {
    const api = this.getAPI();
    const race = api.raceInformation.getRace(position.row);
    const betRow = api.betInformation.getRow(position.row);

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);

    if (start) {
      return start.getHorse();
    }
  }

  horseIsInMyHorses(horse) {
    const atgHorseId = horse?.getAtgHorseId();
    return Boolean(
      atgHorseId &&
        monitor.getHorseByAtgId(getStore().getState(), { atgHorseId }),
    );
  }

  onGridRowOrderChanged(row, indexMap) {
    const betRow = this.getAPI().betInformation.getRow(row);
    betRow.changeOrder(indexMap);
    this.changeOwnReductionOrder(row, indexMap);
    this.getGrid().updateRow(row);
    this.getAPI().scheduleBetUpdate(
      this.requestDividendsInUpdate() ? DIVIDENDS : undefined,
    );
  }

  onGridOwnProfitPercentButtonPressed(position, callback) {
    Utils.callDelegate(
      this.delegate,
      'onViewPercentageSinglePressed',
      position,
      callback,
    );
  }

  makeHorseButtons(position) {
    // If there's no horse monitoring (i.e. Norway) just add Info button
    if (!INCLUDE_HORSE_MONITORING) {
      return [
        {
          title: Text.get('grid-cell-info'),
          onPress: this.onGridInfoButtonPressed,
        },
      ];
    }

    // Otherwise (i.e. Sweden) insert the Info button and also the Manage Horse
    // button if the horse has an ATG id. Invert button colors if horse is in My Horses.
    const horse = this.getHorseAtPosition(position);
    const isInMyHorses = this.horseIsInMyHorses(horse);
    const extraClassNames = isInMyHorses ? ['inverted'] : [];
    const buttonArray = [
      {
        title: Text.get('grid-cell-info'),
        extraClassNames,
        onPress: this.onGridInfoButtonPressed,
      },
    ];
    if (horse.getAtgHorseId() > 0) {
      buttonArray.push({
        icon: isInMyHorses ? 'button-edit-white' : 'button-edit',
        extraClassNames,
        onPress: this.onGridManageHorseButtonPressed,
      });
    }
    return buttonArray;
  }

  onGridInfoButtonPressed(position) {
    Utils.callDelegate(this.delegate, 'onViewHorseInfoSinglePressed', position);
  }

  onGridLastButtonPressed(position) {
    this.getGrid().closeHighlight();
    const isDividendReductionView = this.isInstanceOf(
      'view-dividend-reduction',
    );
    const isFactorReductionView = this.isInstanceOf('view-factor-reduction');

    const willShowLockedAlert = this.showAlertWhenLocked();
    const willShowGuaranteeAlert =
      isFactorReductionView && this.showAlertWhenGuaranteeSet();

    if (!willShowLockedAlert && !willShowGuaranteeAlert) {
      const row = this.getAPI().betInformation.getRow(position.row);
      row.setNumberOfHorses(position.column + 1);
      this.getGrid().updateRow(position.row);
      this.getAPI().scheduleBetUpdate(isDividendReductionView);
    }
  }

  onGridManageHorseButtonPressed(position) {
    this.getGrid().closeHighlight();
    const api = this.getAPI();
    const race = api.raceInformation.getRace(position.row);
    const betRow = api.betInformation.getRow(position.row);
    const betKey = api.betInformation.getBetKey();
    const betType = betKey.getBetType();

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);
    const legNumber =
      betType === 'T' ? race.getRaceNumber() : race.getLegNumber();

    if (start) {
      const horse = start.getHorse();
      if (horse) {
        api.loadBetKeyInfo(betKey, betKeyInfo => {
          let raceName = '';
          if (betKeyInfo) {
            const raceTime = race.getTime()?.getTime();
            const raceTimeStr = raceTime ? `, ${formatDateISO(raceTime)}` : '';
            raceName = `${betKeyInfo
              .getTrack()
              .getName()} lopp ${race.getRaceNumber()}${raceTimeStr}`;
          }
          const horseName = horse.getName();
          const atgHorseId = horse.getAtgHorseId();
          const roundSlug = race.getSlug();
          getStore().dispatch(
            showManageHorseDialog({
              horseName,
              raceName,
              atgHorseId,
              roundSlug,
              startNumber,
              legNumber,
            }),
          );
        });
      }
    }
  }

  /**
   * Select all horses in every race up to and including the
   * number indicated by the clicked column header
   * @param  {jQuery object}  $cell            header cell clicked
   */
  onGridHeaderCellPressed($cell) {
    const willShowLockedAlert = this.showAlertWhenLocked();
    const isDividendReductionView = this.isInstanceOf(
      'view-dividend-reduction',
    );

    if (!willShowLockedAlert) {
      const api = this.getAPI();
      const grid = this.getGrid();
      const column = Number.parseInt($cell.text(), 10);
      const rows = api.betInformation.getRows();

      const shouldMark = rows.some(function (row) {
        return row.getNumberOfHorses() !== column;
      });

      let confirmTitle;
      let confirmBody;

      if (shouldMark) {
        confirmTitle = Text.get('dialog-confirm-mark-all-horses-title');
        confirmBody =
          column === 1
            ? Text.get('dialog-confirm-mark-all-horses-first')
            : Text.get('dialog-confirm-mark-all-horses', `1-${column}`);
      } else {
        confirmTitle = Text.get('dialog-confirm-clear-all-horses-title');
        confirmBody = Text.get('dialog-confirm-clear-all-horses');
      }

      getStore().dispatch(
        showConfirmDialog({
          onConfirm: () => {
            rows.forEach(function (row, index) {
              if (shouldMark) {
                const maxNumberOfHorses = api.raceInformation
                  .getRace(index)
                  .getNumberOfStarts();
                row.setNumberOfHorses(Math.min(column, maxNumberOfHorses));
              } else {
                row.setNumberOfHorses(0);
              }
              grid.updateRow(index);
            });
            this.getAPI().scheduleBetUpdate(isDividendReductionView);
          },
          title: confirmTitle,
          details: confirmBody,
          positive: Text.get('dialog-confirm-confirm'),
          negative: Text.get('dialog-confirm-cancel'),
        }),
      );
    }
  }

  onReplacementJockeyHover(jockey, position, showPopup) {
    if (device.isTouch()) {
      return;
    }

    if (this._replacementJockeyPopupInfo && showPopup === false) {
      this._replacementJockeyPopupInfo.close();
      return;
    }

    const content = Text.get('grid-cell-jockey-replacement') + jockey;

    this._replacementJockeyPopupInfo = new PopupInfo(content, position, 600);
  }

  /**
   * Queries the view whether it can change or not.
   * @return {object} Two parameters are required in the object,
   * ok and reason. If ok is true, it means change the view, the opposite
   * otherwise. If ok is set to false, it would be nice to populate reason
   * to inform the user.
   */
  canChangeView() {
    return {
      ok: true,
      reason: {
        title: '',
        details: '',
      },
    };
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoHidden() {
    return false;
  }

  getBottomInfoContent() {
    return $('<span>BaseView.getBottomInfoContent</span>');
  }

  getBottomInfoTitle() {
    return Text.get('bottom-info-header-tab-title-requirements');
  }

  getBottomInfoInfoLabel() {
    const api = this.getAPI();
    const gameInformation = api.gameInformation;

    if (gameInformation) {
      const numberOfRows = gameInformation.getNumberOfRows();

      if (numberOfRows) {
        return Text.get('bottom-info-header-info-label');
      }
    }

    return '';
  }

  getBottomInfoInfoItems() {
    const api = this.getAPI();

    const gameInformation = api.gameInformation;
    const raceInformation = api.raceInformation;

    const betKey = api.betInformation.getBetKey();
    let betType;

    if (betKey) {
      const betTypeCode = betKey.getBetType();
      betType = BetType.getBetType(betTypeCode);
    }

    const turnovers = [];

    if (gameInformation) {
      const singleRace = betType ? betType.hasSingleRace() : false;

      let gameRow, race, value, turnover, raceNumber;

      if (singleRace) {
        gameRow = gameInformation.getRow(0);
        race = raceInformation.getRace(0);

        if (gameRow && race) {
          turnover = gameRow.getWinnerTurnover();

          value = turnover
            ? formatMoney(turnover.getSum(), turnover.getCurrency())
            : '-';

          raceNumber = race ? race.getRaceNumber() : 1;

          turnovers.push({
            label: Text.get('bottom-info-header-info-item-race', raceNumber),
            value: value,
            raceNumber: raceNumber,
          });
        }
      } else {
        const numberOfRows = raceInformation.getNumberOfRaces();

        for (let n = 0; n < numberOfRows; n++) {
          gameRow = gameInformation.getRow(n);
          race = raceInformation.getRace(n);

          if (!(gameRow && race)) {
            continue;
          }

          const legNr = n + 1;

          value = '-';

          if (gameRow) {
            turnover = gameRow.getWinnerTurnover();

            if (turnover) {
              value = formatMoney(turnover.getSum(), turnover.getCurrency());
            }
          }

          raceNumber = race ? race.getRaceNumber() : legNr;

          turnovers.push({
            label: Text.get('bottom-info-header-info-item-leg', legNr),
            value: value,
            raceNumber: raceNumber,
          });
        }
      }
    }

    return turnovers;
  }

  // ---------------------------------------- VIEW INDEX / NUMBER ----------------------------------------

  getIndex() {
    return this.index || 0;
  }

  getNumber() {
    return this.getIndex() + 1;
  }

  // ---------------------------------------- INPUTS ----------------------------------------

  addLegNumbersInputs($row, legNumbers) {
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const $raceSelectors = $row.find('.race-selector');

    $raceSelectors.each(function () {
      const $raceSelector = $(this);
      $raceSelector.empty();

      const inputName = $raceSelector.data('input-name');

      for (let i = 1; i <= numRaces; i++) {
        const $item = StickyTemplate.jq(
          'bottom-conditions-race-selector-item',
          {
            '%race-number%': i,
            '%name%': inputName,
          },
        );
        $raceSelector.append($item);

        if (legNumbers.includes(i)) {
          $item.find('input[type=checkbox]').prop('checked', true);
        }
      }
    });
  }

  getLegNumbers() {
    const legNumbers = [];

    const numberOfRaces = this.getAPI().raceInformation.getNumberOfRaces();
    for (let legNumber = 1; legNumber <= numberOfRaces; legNumber++) {
      legNumbers.push(legNumber);
    }

    return legNumbers;
  }

  setSelectOptions($select, options) {
    $select.empty();

    for (const option of options) {
      const $option = $('<option/>');

      if (option.text != null) {
        $option.text(option.text);
      }

      if (option.value != null) {
        $option.val(option.value);
      }

      $select.append($option);
    }
  }

  addNumberOfRacesInput($parent, from) {
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const sel = 'select.number-of-races';
    const $select = $parent.find(sel).addBack(sel);
    const options = [];

    const fromIndex = from != null ? from : -1;

    for (let n = fromIndex; n <= numRaces; n++) {
      options.push({
        value: n,
        text: n >= 0 ? n : '',
      });
    }

    this.setSelectOptions($select, options);
  }

  addMiscRequireModeInput($parent) {
    const sel = 'select.misc-require-mode';
    const $select = $parent.find(sel).addBack(sel);

    const options = [{ value: '', text: '' }];

    const modes = ReductionMisc.getRequireModes();

    for (const mode of modes) {
      options.push({
        value: mode,
        text: Text.get(`misc-require-mode_${mode}`),
      });
    }

    this.setSelectOptions($select, options);
  }

  addMiscTypeInput($parent) {
    const sel = 'select.misc-type';
    const $select = $parent.find(sel).addBack(sel);

    const options = [{ value: '', text: '' }];

    const modes = ReductionMisc.getTypes();

    for (const mode of modes) {
      options.push({
        value: mode,
        text: Text.get(`misc-type_${mode}`),
      });
    }

    this.setSelectOptions($select, options);
  }

  addOwnEvaluationInput($parent) {
    const numRaces = this.getAPI().raceInformation.getNumberOfRaces();
    const sel = 'select.owneval-select';
    const $select = $parent.find(sel).addBack(sel);

    const options = [{ value: '', text: '' }];

    for (let n = 0; n < numRaces; n++) {
      options.push({
        value: n + 1,
        text: n + 1,
      });
    }

    this.setSelectOptions($select, options);
  }
}
